<template>
  <p>{{ $t("节点配置") }}</p>

  <!-- URL输入 -->
  <t-form-item name="url" :rules="urlRule">
    <template #label>
      <text-with-mark text="URL" />
    </template>
    <t-input v-model="formData.url" maxlength="200" placeholder="https://" />
  </t-form-item>

  <!-- 超时时间输入 -->
  <t-form-item name="callbackTimeout" :rules="callbackTimeoutRule">
    <template #label>
      <text-with-mark :text="$t('超时时间')" />
      <span style="color: #99a2b2; font-size: 12px; margin-left: 8px">
        ({{ $t("毫秒，默认4500ms") }})
      </span>
    </template>
    <t-input
      v-model.number="formData.callbackTimeout"
      :placeholder="$t('请输入')"
    />
  </t-form-item>

  <!-- 鉴权配置 -->
  <div class="label">
    <div style="margin-bottom: 8px">
      {{ $t("鉴权") }}
      <t-popup
        :content="$t('用于验证调用者身份有效性并校验其访问权限')"
        show-arrow
        destroy-on-close
      >
        <InfoCircleIcon class="infoIcon" />
      </t-popup>
    </div>
    <t-switch :value="!!formData.authType" @change="onAuthSwitchChange" />
  </div>

  <div v-if="!!formData.authType">
    <p style="margin-top: 8px; margin-bottom: 8px">
      <text-with-mark :text="$t('鉴权类型')" />
    </p>
    <t-form-item name="type" style="margin-bottom: 0px">
      <t-radio :checked="isBasicAuth" @change="handleBasicAuthChange">
        Basic Auth
      </t-radio>
      <t-radio
        :checked="isBearerAuth"
        style="margin-left: 16px"
        @change="handleBearerAuthChange"
      >
        Bearer
      </t-radio>
      <t-radio
        :checked="isCustomAuth"
        style="margin-left: 16px"
        @change="handleCustomAuthChange"
      >
        {{ $t("自定义") }}
      </t-radio>
    </t-form-item>

    <!-- Basic Auth -->
    <div v-if="formData.authType === 1">
      <p style="margin-top: 8px; margin-bottom: 8px">
        <text-with-mark text="Token" />
      </p>
      <t-form-item
        name="basicAuth.basicToken"
        :rules="requireRule"
        style="margin-bottom: 12px"
      >
        <t-input
          v-model="formData.basicAuth.basicToken"
          maxlength="256"
          :placeholder="$t('请输入')"
        />
      </t-form-item>
    </div>

    <!-- Bearer Token -->
    <div v-if="formData.authType === 2">
      <p style="margin-top: 8px; margin-bottom: 8px">
        <text-with-mark text="Token" />
      </p>
      <t-form-item
        name="bearerAuth.bearerToken"
        :rules="tokenRule"
        style="margin-bottom: 12px"
      >
        <t-input
          v-model="formData.bearerAuth.bearerToken"
          maxlength="256"
          :placeholder="$t('请输入')"
        />
      </t-form-item>
    </div>

    <!-- 自定义 Header/Query -->
    <div v-if="formData.authType === 3 || formData.authType === 4">
      <p style="margin-top: 8px; margin-bottom: 8px">
        <text-with-mark :text="$t('密钥位置')" />
      </p>
      <t-radio-group
        :value="`${formData.authType}`"
        style="margin-bottom: 4px"
        @change="onAuthTypeChange"
      >
        <t-radio value="3">Header</t-radio>
        <t-radio value="4">Query</t-radio>
      </t-radio-group>

      <p style="margin-top: 8px; margin-bottom: 8px">
        <text-with-mark text="Key" />
      </p>
      <t-form-item
        name="customAuth.key"
        :rules="keyRule"
        style="margin-bottom: 12px"
      >
        <t-input
          v-model="formData.customAuth.key"
          maxlength="1024"
          :placeholder="$t('请输入')"
        />
      </t-form-item>
      <p style="margin-top: 8px; margin-bottom: 8px">
        <text-with-mark text="Value" />
      </p>
      <t-form-item
        name="customAuth.value"
        :rules="requireRule"
        style="margin-bottom: 24px"
      >
        <t-input
          v-model="formData.customAuth.value"
          maxlength="1024"
          :placeholder="$t('请输入')"
        />
      </t-form-item>
    </div>
  </div>

  <!-- 输入参数 -->
  <p style="margin-top: 24px">{{ $t("输入参数") }}</p>

  <div>
    <t-table
      v-if="(formData.params ?? []).length > 0"
      class="no-border-table"
      :data="formData.params"
      lazy-load
      size="small"
      :columns="paramsColumns"
      vertical-align="top"
    >
      <template #type="{ rowIndex }">
        <t-form-item :name="`formData.params[${rowIndex}].type`">
          <t-select
            v-model="formData.params[rowIndex].type"
            :options="paramTypeOptions"
            style="width: 150px"
            :placeholder="$t('请选择')"
            @change="onParamTypeChange(rowIndex, $event)"
          />
        </t-form-item>
      </template>
      <template #key="{ rowIndex, row }">
        <t-form-item :name="`formData.params[${rowIndex}].key`">
          <!-- type=1 信息收集变量 -->
          <t-select
            v-if="row.type === 1"
            v-model="formData.params[rowIndex].value"
            :options="infoExtractOptions"
            :placeholder="$t('选择变量')"
            :popup-props="selectPopupProps"
            @change="onInfoExtractVarChange(rowIndex, $event)"
          />
          <!-- type=2 系统变量 -->
          <t-select
            v-else-if="row.type === 2"
            v-model="formData.params[rowIndex].key"
            @change="onSystemVarChange(rowIndex, $event)"
          >
            <t-option
              v-for="item in systemVarsPool"
              :key="item.value"
              :value="item.value"
              :label="item.label"
            >
              <t-tooltip :content="item.tip">{{ item.label }}</t-tooltip>
            </t-option>
          </t-select>
          <!-- type=3 自定义变量（接口调用输出参数） -->
          <t-select
            v-else-if="row.type === 3"
            v-model="formData.params[rowIndex].value"
            :options="apiCallOptions"
            :placeholder="$t('选择变量')"
            :popup-props="selectPopupProps"
            @change="onCustomVarChange(rowIndex, $event)"
          />
          <!-- type=4 固定变量 -->
          <div v-else-if="row.type === 4" class="fixedVar">
            <t-input
              v-model="formData.params[rowIndex].key"
              maxlength="200"
              placeholder="key"
              style="width: 105px"
            />
            <t-input
              v-model="formData.params[rowIndex].value"
              maxlength="200"
              placeholder="value"
              style="width: 105px"
            />
          </div>
        </t-form-item>
      </template>
      <template #operation="{ rowIndex }">
        <CloseCircleIcon
          size="16"
          style="cursor: pointer; vertical-align: bottom"
          @click="deleteParam(rowIndex)"
        />
      </template>
    </t-table>
    <t-form-item :status-icon="false">
      <div v-if="(formData.params?.length ?? 0) < 20">
        <t-link theme="primary" hover="color" @click="addParam">
          {{ $t("添加输入参数") }}
        </t-link>
      </div>
    </t-form-item>
  </div>

  <t-divider style="margin: 8px 0"></t-divider>

  <!-- 输出参数 -->
  <p style="margin-top: 24px; margin-bottom: 8px">{{ $t("输出参数") }}</p>
  <div v-if="(formData.returns ?? []).length > 0">
    <p style="color: #6d778c; margin-bottom: 8px">{{ $t("参数") }}</p>

    <div v-for="(item, index) in formData.returns" :key="index">
      <t-form-item
        :name="`formData.returns[${index}].key`"
        style="margin-bottom: 10px"
      >
        <!-- 使用 varIdList 作为数据源，只显示当前节点定义的变量 -->
        <t-select
          v-model="formData.returns[index].key"
          :placeholder="$t('选择变量')"
          :popup-props="selectPopupProps"
          @change="onReturnVarChange(index, $event)"
          @popupVisibleChange="(visible) => onSelectPopupChange(visible, index)"
        >
          <t-option
            v-for="option in currentNodeVarOptions"
            :key="option.id"
            :value="option.value"
            :label="option.label"
            class="custom-option"
            @mouseenter="() => showEditButton(option)"
            @mouseleave="() => hideEditButton(option)"
          >
            <div
              style="display: flex; justify-content: space-between; width: 100%"
            >
              <span>{{ option.label }}</span>
              <div v-if="option.showButton" style="display: flex; gap: 5px">
                <img
                  :src="editNodeIcon"
                  class="custom-button"
                  @click.stop="handleEditVar(index, option)"
                />
                <img
                  :src="deleteNodeIcon"
                  class="custom-button"
                  @click.stop="handleDeleteVar(index, option)"
                />
              </div>
            </div>
          </t-option>
          <t-option @click.stop="handleCreateVar(index)">
            <span style="padding: 0px; color: #0052d9">
              {{ $t("新建变量") }}
            </span>
          </t-option>
        </t-select>
        <CloseCircleIcon
          size="16"
          style="cursor: pointer; margin-left: 15px; margin-top: -2px"
          @click="deleteReturn(index)"
        />
      </t-form-item>
    </div>
  </div>

  <!-- 变量编辑对话框 -->
  <t-dialog
    v-model:visible="varDialogVisible"
    width="300"
    :header="false"
    @confirm="handleVarDialogConfirm"
  >
    <p v-if="varDialogData.type === 'delete'">{{ $t("确认删除？") }}</p>
    <div v-else>
      <t-input
        v-model="varDialogData.value"
        :placeholder="$t('请输入变量key')"
        :status="varDialogErrorMessage ? 'error' : 'default'"
        maxlength="100"
        @input="varDialogErrorMessage = ''"
      />
      <div v-if="varDialogErrorMessage" class="error-message">
        {{ varDialogErrorMessage }}
      </div>
    </div>
  </t-dialog>

  <div v-if="(formData.returns?.length ?? 0) < 20" style="margin-top: 10px">
    <t-link theme="primary" hover="color" @click="addReturn">
      {{ $t("添加输出参数") }}
    </t-link>
  </div>

  <!-- 异常处理 -->
  <div class="label">
    <div style="margin: 14px 0">
      <span>{{ $t("异常处理") }}</span>
      <t-popup
        :content="
          $t(
            '开启后，接口调用节点将新增异常处理分支。若调用失败，任务流自动进入该分支。',
          )
        "
        show-arrow
        destroy-on-close
      >
        <InfoCircleIcon class="infoIcon" />
      </t-popup>
    </div>
    <t-switch :value="!!formData.fallbackSwitch" @change="onSwitchChange" />
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  reactive,
  watch,
  inject,
  ref,
  getCurrentInstance,
  nextTick,
  type Ref,
} from "vue";
import { CloseCircleIcon, InfoCircleIcon } from "tdesign-icons-vue-next";
import { trim } from "lodash-es";
import { systemVarsPool } from "@/utils/const";
import editNodeIcon from "@/assets/icons/edit-node-icon.svg";
import deleteNodeIcon from "@/assets/icons/delete-node-icon.svg";
import {
  validateBearerToken,
  validateHeaderKey,
  validateQueryKey,
  validateUrl,
} from "@/utils/rules";
import { t } from "@/utils/i18n";
import { getRelationUUID, getUUID } from "@/utils";
import { FALLBACK_ERROR, FALLBACK_SUCCESS } from "../const";
import { extractInfoNodesData } from "@/flow/flow-util/node/utils";

const emit = defineEmits(["updateData"]);

// 变量池，用于获取info_id和name的对应关系
const variablePool = inject<any>("variablePool", { value: [] });
const xGraph = inject("xGraph");

// 变量ID映射列表（智能任务流使用）
const varIdList = inject<
  Ref<Array<{ id: string; nodeId: string; varName: string }>>
>("varIdList", ref([]));

// 信息提取节点的变量映射：name -> info_id
const infoNameToIdMap = ref<Record<string, string>>({});
// 自定义变量（新建的变量）的映射：name -> info_id
const customVarToIdMap = ref<Record<string, string>>({});
// 信息提取节点的选项列表（type=1 使用）
const infoExtractOptions = ref<
  Array<{
    value: string;
    label: string;
    tip: string;
  }>
>([]);
// 接口调用节点的输出参数选项列表（type=3 使用）
const apiCallOptions = ref<
  Array<{
    value: string;
    label: string;
    tip: string;
  }>
>([]);

// 当前节点的变量选项列表（用于输出参数选择，只显示当前节点定义的变量）
// 从 varIdList 中过滤出当前节点的变量，实现节点级别的变量隔离
const currentNodeVarOptions = computed(() => {
  if (!props.currentNodeId) {
    return [];
  }
  return varIdList.value
    .filter((v) => v.nodeId === props.currentNodeId)
    .map((v) => ({
      id: v.id,
      value: v.varName,
      label: v.varName,
      showButton: false, // 用于控制编辑/删除按钮的显示
    }));
});

// 变量编辑对话框相关状态
const varDialogVisible = ref(false);
const varDialogData = reactive<{
  type: "create" | "edit" | "delete";
  value: string;
  index: number;
  option?: { id: string; value: string; label: string };
}>({
  type: "create",
  value: "",
  index: -1,
});
const varDialogErrorMessage = ref("");

// 下拉框的 popupProps，使下拉框宽度与输入框保持一致
// 用于信息收集变量、自定义变量和输出参数
const selectPopupProps = {
  overlayInnerStyle: (trigger: HTMLElement) => ({
    width: `${trigger.offsetWidth}px`,
  }),
};

const props = defineProps<{
  data: {
    url?: string;
    callbackTimeout?: number;
    params?: Array<{
      type: number;
      key: string;
      value?: string;
    }>;
    returns?: Array<{
      variable_id: string;
      key: string;
    }>;
    fallbackSwitch?: number;
    branches?: Array<{ content: string; id: string; type: 0 | 1 }>;
    authType?: number;
    basicAuth?: {
      basicToken?: string;
    };
    bearerAuth?: {
      bearerToken?: string;
    };
    customAuth?: {
      key: string;
      value: string;
    };
  };
  currentNodeId?: string;
}>();

const formData = reactive({
  url: props.data.url ?? "",
  callbackTimeout: props.data.callbackTimeout ?? 4500,
  params: props.data.params ?? [
    {
      type: 4, // 默认使用固定变量
      key: "",
      value: "",
    },
  ],
  returns: props.data.returns ?? [
    {
      variable_id: "",
      key: "",
    },
  ],
  fallbackSwitch: props.data.fallbackSwitch ?? 0,
  branches:
    props.data.branches && props.data.branches.length > 0
      ? props.data.branches
      : [
          {
            id: getRelationUUID(),
            content: t("调用成功"),
            type: FALLBACK_SUCCESS,
          },
        ],
  authType: props.data.authType ?? 0,
  basicAuth: props.data.basicAuth ?? { basicToken: "" },
  bearerAuth: props.data.bearerAuth ?? { bearerToken: "" },
  customAuth: props.data.customAuth ?? { key: "", value: "" },
  async: false, // 固定为false
});

// 验证规则
const urlRule = [
  {
    validator: (url) => !!trim(url),
    message: t("不能为空"),
    trigger: "change",
  },
  {
    validator: (url) => validateUrl(url),
    message: t("请输入正确url地址"),
    trigger: "change",
  },
];

const requireRule = [
  {
    validator: (val) => !!trim(val),
    message: t("不能为空"),
    trigger: "change",
  },
];

const tokenRule = [
  {
    validator: (token) => !!trim(token),
    message: t("不能为空"),
    trigger: "change",
  },
  {
    validator: (token) => validateBearerToken(token),
    message: t("格式错误"),
    trigger: "change",
  },
];

const keyRule = [
  {
    validator: (key) => !!trim(key),
    message: t("不能为空"),
    trigger: "change",
  },
  {
    validator: (key) => {
      return formData.authType === 3
        ? validateHeaderKey(key)
        : validateQueryKey(key);
    },
    message: t("格式错误"),
    trigger: "change",
  },
];

const callbackTimeoutRule = [
  {
    validator: (value) => {
      // 允许为空，空值会在watch中设置为默认值
      if (value === null || value === undefined || value === "") {
        return true;
      }
      // 检查是否为有效数字
      const numValue = Number(value);
      if (isNaN(numValue) || numValue <= 0) {
        return false;
      }
      // 检查是否超过30秒（30000毫秒）
      return numValue <= 30000;
    },
    message: t("超时时间不能超过30秒（30000毫秒）"),
    trigger: "change",
  },
];

// 清空所有鉴权字段
const clearAllAuthFields = () => {
  formData.basicAuth.basicToken = "";
  formData.bearerAuth.bearerToken = "";
  formData.customAuth.key = "";
  formData.customAuth.value = "";
};

// 重置表单验证状态
const resetFormValidation = async () => {
  await nextTick();
  try {
    const instance = getCurrentInstance();
    if (instance) {
      // 向上查找父组件，找到包含 formRef 的组件
      let parent = instance.parent;
      while (parent) {
        const setupState = (parent as any).setupState;
        const exposed = (parent as any).exposed;
        const refs = (parent as any).refs;

        const formRef =
          setupState?.formRef || exposed?.formRef || refs?.formRef;

        if (formRef && typeof formRef.clearValidate === "function") {
          // 清除所有鉴权相关字段的验证状态
          formRef.clearValidate([
            "basicAuth.basicToken",
            "bearerAuth.bearerToken",
            "customAuth.key",
            "customAuth.value",
          ]);
          break;
        }

        parent = parent.parent;
      }
    }
  } catch (error) {
    // 如果无法访问表单实例，忽略错误（字段值已清空，验证状态会自动更新）
    console.warn("无法重置表单验证状态:", error);
  }
};

// 鉴权开关
const onAuthSwitchChange = async (value) => {
  formData.authType = value ? 2 : 0;
  // 切换鉴权开关时清空所有字段
  clearAllAuthFields();
  await resetFormValidation();
};

// 鉴权类型判断
const isBasicAuth = computed(() => formData.authType === 1);
const isBearerAuth = computed(() => formData.authType === 2);
const isCustomAuth = computed(() => formData.authType > 2);

// 鉴权类型切换
const handleBasicAuthChange = async (checked) => {
  if (checked) {
    formData.authType = 1;
    clearAllAuthFields();
    await resetFormValidation();
  }
};

const handleBearerAuthChange = async (checked) => {
  if (checked) {
    formData.authType = 2;
    clearAllAuthFields();
    await resetFormValidation();
  }
};

const handleCustomAuthChange = async (checked) => {
  if (checked) {
    formData.authType = 4; // 默认选择 Header
    clearAllAuthFields();
    await resetFormValidation();
  }
};

const onAuthTypeChange = async (value) => {
  formData.authType = +value;
  // Header ↔ Query 切换时也清空所有字段
  clearAllAuthFields();
  await resetFormValidation();
};

// 输入参数类型选项
const paramTypeOptions = [
  { value: 1, label: t("信息收集变量") },
  { value: 2, label: t("系统变量") },
  { value: 3, label: t("自定义变量") },
  { value: 4, label: t("固定变量") },
];

// 输入参数表格列
const paramsColumns = [
  {
    colKey: "type",
    title: t("参数类型"),
    width: 165,
  },
  {
    colKey: "key",
    title: t("参数"),
  },
  {
    colKey: "operation",
    width: 30,
    align: "right",
  },
];

// 参数类型改变
const onParamTypeChange = (index, value) => {
  formData.params[index].type = value;
  formData.params[index].key = "";
  formData.params[index].value = "";
};

// 信息收集变量改变（type=1）
const onInfoExtractVarChange = (index, info_id) => {
  // 从 infoExtractOptions 中找到对应的 label（变量名）作为 key
  const option = infoExtractOptions.value.find((opt) => opt.value === info_id);
  if (option) {
    // 提取变量名（label 格式为 "节点名/变量名"）
    const varName = option.label.split("/").pop() || option.label;
    formData.params[index].key = varName;
    formData.params[index].value = info_id;
  } else {
    // 如果找不到，说明数据异常，清空
    formData.params[index].key = "";
    formData.params[index].value = "";
  }
};

// 系统变量改变（type=2）
const onSystemVarChange = (index, value) => {
  formData.params[index].key = value;
  formData.params[index].value = value;
};

// 自定义变量改变（type=3，接口调用输出参数）
const onCustomVarChange = (index, info_id) => {
  console.log(apiCallOptions.value, "apiCallOptions.value");
  // 从 apiCallOptions 中找到对应的 label（变量名）作为 key
  const option = apiCallOptions.value.find((opt) => opt.value === info_id);
  if (option) {
    // 提取变量名（label 格式为 "节点名/变量名"）
    const varName = option.label.split("/").pop() || option.label;
    formData.params[index].key = varName;
    formData.params[index].value = info_id;
  } else {
    // 如果找不到，说明数据异常，清空
    formData.params[index].key = "";
    formData.params[index].value = "";
  }
};

// 添加输入参数
const addParam = () => {
  formData.params.push({
    type: 4, // 默认使用固定变量
    key: "",
    value: "",
  });
};

// 删除输入参数
const deleteParam = (index: number) => {
  formData.params.splice(index, 1);
};

// 显示/隐藏编辑按钮（用于鼠标悬停效果）
const showEditButton = (option) => {
  option.showButton = true;
};
const hideEditButton = (option) => {
  option.showButton = false;
};

// 处理下拉框打开/关闭事件，重置按钮显示状态
const onSelectPopupChange = (visible, index) => {
  if (!visible) {
    // 关闭时重置所有选项的按钮显示状态
    currentNodeVarOptions.value.forEach((opt) => {
      opt.showButton = false;
    });
  }
};

// 处理新建变量
const handleCreateVar = (index) => {
  varDialogData.type = "create";
  varDialogData.value = "";
  varDialogData.index = index;
  varDialogErrorMessage.value = "";
  varDialogVisible.value = true;
};

// 处理编辑变量
const handleEditVar = (index, option) => {
  varDialogData.type = "edit";
  varDialogData.value = option.label;
  varDialogData.index = index;
  varDialogData.option = option;
  varDialogErrorMessage.value = "";
  varDialogVisible.value = true;
};

// 处理删除变量
const handleDeleteVar = (index, option) => {
  varDialogData.type = "delete";
  varDialogData.index = index;
  varDialogData.option = option;
  varDialogVisible.value = true;
};

// 处理变量对话框确认
const handleVarDialogConfirm = () => {
  const { type, value, index, option } = varDialogData;
  const trimmedValue = trim(value);

  if (type === "delete") {
    // 删除变量
    if (option && option.id && props.currentNodeId) {
      // 从 varIdList 中删除
      const varIndex = varIdList.value.findIndex(
        (v) => v.id === option.id && v.nodeId === props.currentNodeId,
      );
      if (varIndex !== -1) {
        varIdList.value.splice(varIndex, 1);
      }
      // 清理所有输出参数中使用该变量的地方
      formData.returns.forEach((returnItem) => {
        if (returnItem.variable_id === option.id) {
          returnItem.key = "";
          returnItem.variable_id = "";
        }
      });
      // 清理所有输入参数中对该变量的引用
      formData.params.forEach((param) => {
        if (param.type === 3 && param.value === option.id) {
          param.value = "";
          param.key = "";
        }
      });
    }
    varDialogVisible.value = false;
    return;
  }

  // 创建或编辑变量需要验证
  if (!trimmedValue) {
    varDialogErrorMessage.value = t("不能为空");
    return;
  }

  // 检查变量名称是否与系统变量重复
  const isSystemVarName = systemVarsPool.some(
    (sysVar) => sysVar.value === trimmedValue,
  );
  if (isSystemVarName) {
    varDialogErrorMessage.value = t("该变量名称已存在，请尝试其他名称");
    return;
  }

  // 检查是否与其他变量重名（同一节点内）
  if (
    type === "create" ||
    (type === "edit" && option && option.value !== trimmedValue)
  ) {
    const existingVar = varIdList.value.find(
      (v) => v.nodeId === props.currentNodeId && v.varName === trimmedValue,
    );
    if (existingVar) {
      varDialogErrorMessage.value = t("该变量名称已存在，请尝试其他名称");
      return;
    }
  }

  // 清除错误信息
  varDialogErrorMessage.value = "";

  if (type === "create") {
    // 新建变量：直接调用 onReturnVarChange 处理
    onReturnVarChange(index, trimmedValue);
  } else if (type === "edit" && option) {
    // 编辑变量：更新 varIdList 中的变量名
    const existingVar = varIdList.value.find(
      (v) => v.id === option.id && v.nodeId === props.currentNodeId,
    );
    if (existingVar) {
      existingVar.varName = trimmedValue;
      // 更新 formData.returns 中的 key
      formData.returns[index].key = trimmedValue;
      // 更新所有使用该变量的地方
      formData.params.forEach((param) => {
        if (param.type === 3 && param.value === option.id) {
          // 从 varIdList 中获取最新的变量名
          const varInfo = varIdList.value.find((v) => v.id === option.id);
          if (varInfo) {
            param.key = varInfo.varName;
          }
        }
      });
    }
  }

  varDialogVisible.value = false;
};

// 返回值变量改变（从变量池选择）
const onReturnVarChange = (index, newKey) => {
  if (!props.currentNodeId) {
    console.warn("currentNodeId is missing");
    return;
  }

  // 如果 newKey 为空，直接返回（可能是异常触发，忽略）
  if (!newKey) {
    return;
  }

  const currentVariableId = formData.returns[index].variable_id;

  // 情况1：已有 variable_id，执行修改操作（重命名）
  if (currentVariableId) {
    // 根据 variable_id 和 nodeId 查找并更新变量名
    const existingVar = varIdList.value.find(
      (v) => v.id === currentVariableId && v.nodeId === props.currentNodeId,
    );
    if (existingVar) {
      existingVar.varName = newKey;
    } else {
      // 如果找不到，可能是数据不一致，创建新的映射
      varIdList.value.push({
        id: currentVariableId,
        nodeId: props.currentNodeId,
        varName: newKey,
      });
    }
    // variable_id 保持不变
  }
  // 情况2：没有 variable_id，新建变量
  else {
    const newVariableId = `userdefine-${getUUID()}`;

    // 添加到数组
    varIdList.value.push({
      id: newVariableId,
      nodeId: props.currentNodeId,
      varName: newKey,
    });

    // 设置 variable_id 和 key，确保新建的变量默认被选中
    formData.returns[index].variable_id = newVariableId;
    formData.returns[index].key = newKey;
    console.log("创建新变量:", newVariableId);
  }
};

// 添加输出参数
const addReturn = () => {
  formData.returns.push({
    variable_id: "",
    key: "",
  });
};

// 删除输出参数
const deleteReturn = (index: number) => {
  const returnItem = formData.returns[index];

  console.log("deleteReturn 调试:", {
    index,
    returnItem,
    currentNodeId: props.currentNodeId,
    varIdListBefore: JSON.parse(JSON.stringify(varIdList.value)),
  });

  // 如果有 variable_id 和 nodeId，从 varIdList 中删除
  if (returnItem.variable_id && props.currentNodeId) {
    const varIndex = varIdList.value.findIndex(
      (v) =>
        v.id === returnItem.variable_id && v.nodeId === props.currentNodeId,
    );
    console.log("找到的索引:", varIndex);
    if (varIndex !== -1) {
      varIdList.value.splice(varIndex, 1);
      console.log(
        "删除后的 varIdList:",
        JSON.parse(JSON.stringify(varIdList.value)),
      );
    } else {
      console.warn("未找到对应的变量映射", {
        variable_id: returnItem.variable_id,
        currentNodeId: props.currentNodeId,
      });
    }
  }

  // 级联删除：清理所有输入参数中对该变量的引用
  if (returnItem.variable_id) {
    formData.params.forEach((param) => {
      if (param.type === 3 && param.value === returnItem.variable_id) {
        console.log("清理输入参数中的引用:", param);
        param.value = "";
        param.key = "";
      }
    });
  }

  formData.returns.splice(index, 1);

  console.log("deleteReturn 完成");
};

// 异常处理开关
const onSwitchChange = (value) => {
  formData.fallbackSwitch = value ? 1 : 0;
  if (value) {
    formData.branches = [
      { id: getRelationUUID(), content: t("调用成功"), type: FALLBACK_SUCCESS },
      { id: getRelationUUID(), content: t("异常处理"), type: FALLBACK_ERROR },
    ];
  } else {
    // 未开启异常处理时，仍然需要有一个默认的成功分支，用于生成 branchId
    formData.branches = [
      { id: getRelationUUID(), content: t("调用成功"), type: FALLBACK_SUCCESS },
    ];
  }
};

// 监听变量池变化，为新建的变量生成 info_id
watch(
  () => variablePool.value,
  (newVal, oldVal) => {
    if (!Array.isArray(newVal)) return;
    // 找出新增的变量
    const oldVars = Array.isArray(oldVal) ? oldVal : [];
    const newVars = newVal.filter((v) => !oldVars.includes(v));
    // 为新变量生成 variable_id
    // 使用 userdefine- 前缀，仅用于接口调用节点的 returns
    newVars.forEach((varName) => {
      if (!customVarToIdMap.value[varName]) {
        customVarToIdMap.value[varName] = `userdefine-${getUUID()}`;
      }
    });
  },
  { immediate: true },
);

// 监听 xGraph 和 currentNodeId 变化，更新信息提取节点的变量映射
watch(
  [xGraph as any, () => props.currentNodeId],
  ([newVal, currentNodeId]) => {
    if (!newVal) return;

    const nodes = newVal.getNodes();
    const infoNodesData = extractInfoNodesData(nodes);

    // 建立 name -> info_id 的映射
    const map: Record<string, string> = {};
    infoNodesData.forEach((node) => {
      node.infos.forEach((info) => {
        map[info.name] = info.id;
      });
    });

    infoNameToIdMap.value = map;

    // 收集接口调用节点的输出参数（returns）
    const apiCallNodesData: Array<{
      nodeName: string;
      returns: Array<{ variable_id: string; key: string }>;
    }> = [];
    nodes.forEach((node) => {
      const nodeModel = node.getModel ? node.getModel() : node;
      const nodeData = nodeModel.nodeData;
      // 收集所有接口调用节点的输出参数，但排除当前节点的输出参数
      // 因为输入参数不能依赖同一节点的输出参数（输出参数在执行后才产生）
      // 只有当 currentNodeId 有值时才进行过滤，避免 undefined 导致的过滤失效
      if (
        nodeData.nodeType === "interfaceInvocationNode" &&
        nodeData.returns &&
        Array.isArray(nodeData.returns) &&
        (!currentNodeId || nodeModel.id !== currentNodeId) // 排除当前节点
      ) {
        apiCallNodesData.push({
          nodeName: nodeData.nodeName || "",
          returns: nodeData.returns.filter(
            (r) =>
              r.variable_id && r.key && r.variable_id.startsWith("userdefine-"),
          ),
        });
      }
    });

    // 构建 infoExtractOptions，只包含信息提取节点的变量（type=1 使用）
    infoExtractOptions.value = infoNodesData
      .map((node) => {
        return node.infos.map((info) => ({
          value: info.id, // info_id
          label: node.nodeName + "/" + info.name, // 节点名/变量名
          tip: node.nodeName,
        }));
      })
      .flat();

    // 构建 apiCallOptions，只包含接口调用节点的输出参数（type=3 使用）
    // 从 varIdList 获取最新的变量名（响应式更新）
    apiCallOptions.value = apiCallNodesData
      .map((node) => {
        return node.returns.map((ret) => {
          // 从 varIdList 中获取最新的变量名
          const varInfo = varIdList.value.find((v) => v.id === ret.variable_id);
          const varName = varInfo ? varInfo.varName : ret.key;

          return {
            value: ret.variable_id, // variable_id
            label: node.nodeName + "/" + varName, // 节点名/最新变量名
            tip: node.nodeName,
          };
        });
      })
      .flat();

    // 更新现有的 returns，确保 key 对应的 variable_id 正确
    formData.returns.forEach((returnItem) => {
      if (returnItem.key && !returnItem.variable_id && currentNodeId) {
        // 从 varIdList 中查找（智能任务流使用）
        const existingVar = varIdList.value.find(
          (v) => v.nodeId === currentNodeId && v.varName === returnItem.key,
        );

        if (existingVar) {
          // 找到了，使用现有的 variable_id
          returnItem.variable_id = existingVar.id;
        } else {
          // 没找到，生成新的 variable_id
          const variable_id = `userdefine-${getUUID()}`;
          varIdList.value.push({
            id: variable_id,
            nodeId: currentNodeId,
            varName: returnItem.key,
          });
          returnItem.variable_id = variable_id;
        }
      }
    });

    // 更新现有的 params（type=1 和 type=3），确保 value 是 info-xxx 格式
    formData.params.forEach((param) => {
      if (param.type === 1) {
        // type=1: 信息收集变量
        // 如果 value 不是 info-xxx 格式，尝试从 key（变量名）找到对应的 info_id
        if (param.key && (!param.value || !param.value.startsWith("info-"))) {
          // 从信息提取节点映射中查找
          const info_id = map[param.key];
          if (info_id) {
            param.value = info_id;
          }
        }
        // 检查 value 是否在 infoExtractOptions 中存在，如果不存在则清空
        if (param.value && param.value.startsWith("info-")) {
          const exists = infoExtractOptions.value.some(
            (opt) => opt.value === param.value,
          );
          if (!exists) {
            // 变量已失效，清空值
            param.value = "";
            param.key = "";
          }
        }
      } else if (param.type === 3) {
        // type=3: 自定义变量（接口调用输出参数）
        // 如果 value 不是 userdefine-xxx 格式，尝试从 key（变量名）找到对应的 variable_id
        if (
          param.key &&
          (!param.value || !param.value.startsWith("userdefine-"))
        ) {
          // 从接口调用节点的输出参数中查找
          const option = apiCallOptions.value.find(
            (opt) => opt.label.split("/").pop() === param.key,
          );
          if (option) {
            param.value = option.value;
          }
        }
        // 检查 value 是否在 apiCallOptions 中存在，如果不存在则清空
        if (param.value && param.value.startsWith("userdefine-")) {
          const exists = apiCallOptions.value.some(
            (opt) => opt.value === param.value,
          );
          if (!exists) {
            // 变量已失效，清空值
            param.value = "";
            param.key = "";
          }
        }
      }
    });
  },
  { immediate: true },
);

watch(
  formData,
  (newVal) => {
    // 确保callbackTimeout有效值
    if (!newVal.callbackTimeout || newVal.callbackTimeout <= 0) {
      newVal.callbackTimeout = 4500;
    }

    // 不在这里过滤，让用户在编辑过程中保留空项
    // 过滤逻辑将在真正提交数据时执行（在 edit-node-component 的 handleFormDataUpdate 中）
    emit("updateData", newVal);
  },
  { deep: true },
);
</script>

<style lang="less" scoped>
:deep(.flow-t-table td) {
  border: none;
}
:deep(.flow-t-table th) {
  border: none;
}
:deep(.flow-t-table.flow-t-size-s th) {
  padding-left: 0px;
  padding-top: 6px;
  padding-bottom: 6px;
}
:deep(.flow-t-table.flow-t-size-s td) {
  padding: 0px;
}
:deep(.flow-t-table tr) {
  height: 42px;
}
:deep(.flow-t-table .flow-t-table__header tr) {
  height: 30px;
}
.fixedVar {
  display: flex;
  flex: 1 1;
  justify-content: space-between;
}
.label {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.infoIcon {
  color: #99a2b2;
  margin-top: -3px;
  margin-left: 4px;
}
.custom-option {
  display: flex;
  width: 100%;
  justify-content: space-between;
}
.custom-button {
  vertical-align: middle;
  cursor: pointer;
  margin-left: 5px;
  width: 16px;
  height: 16px;
}
.error-message {
  color: var(--td-error-color);
  font-size: 12px;
  margin-top: 4px;
  line-height: 1.5;
}
</style>
